import React, { useState } from 'react';
import { X } from 'lucide-react';
import { ComplianceObligationInstance, WaiverRequestType } from './types';

interface Props {
  instance: ComplianceObligationInstance;
  onClose: () => void;
  onFulfill: (notes: string, completedBy: string) => void;
  onVerify: (data: {
    verification_status: 'PASSED' | 'FAILED' | 'PARTIAL';
    verifier_id: string;
    verifier_name: string;
    verification_notes: string;
  }) => void;
  onAddEvidence: (data: {
    title: string;
    file_name: string;
    file_type: string;
    uploaded_by: string;
    notes?: string;
  }) => void;
  onRequestWaiver: (data: {
    request_type: WaiverRequestType;
    requested_due_date?: string;
    justification: string;
    compensating_controls?: string;
    requested_by: string;
  }) => void;
  onApproveWaiver: (waiverId: string, approvedBy: string, notes?: string) => void;
}

export const ObligationInstanceDetailModal: React.FC<Props> = ({
  instance,
  onClose,
  onFulfill,
  onVerify,
  onAddEvidence,
  onRequestWaiver,
  onApproveWaiver,
}) => {
  const [activeTab, setActiveTab] = useState<'OVERVIEW' | 'FULFILL' | 'VERIFY' | 'EVIDENCE' | 'WAIVERS'>('OVERVIEW');

  // Fulfillment form
  const [fulfillNotes, setFulfillNotes] = useState('');
  const [fulfillCompletedBy, setFulfillCompletedBy] = useState(instance.assigned_executor_name);

  // Verification form
  const [verifStatus, setVerifStatus] = useState<'PASSED' | 'FAILED' | 'PARTIAL'>('PASSED');
  const [verifierName, setVerifierName] = useState('Compliance Monitoring Auditor');
  const [verifNotes, setVerifNotes] = useState('');

  // Evidence form
  const [eviTitle, setEviTitle] = useState('');
  const [eviFileName, setEviFileName] = useState('');
  const [eviFileType, setEviFileType] = useState('application/pdf');

  // Waiver form
  const [waiverType, setWaiverType] = useState<WaiverRequestType>('EXTENSION');
  const [waiverDueDate, setWaiverDueDate] = useState('');
  const [waiverJustification, setWaiverJustification] = useState('');
  const [compensatingControls, setCompensatingControls] = useState('');

  const handleFulfillSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!fulfillNotes || !fulfillCompletedBy) return;
    onFulfill(fulfillNotes, fulfillCompletedBy);
    setFulfillNotes('');
  };

  const handleVerifySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!verifierName || !verifNotes) return;
    onVerify({
      verification_status: verifStatus,
      verifier_id: 'USR-VERIFIER',
      verifier_name: verifierName,
      verification_notes: verifNotes,
    });
    setVerifNotes('');
  };

  const handleEvidenceSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!eviTitle || !eviFileName) return;
    onAddEvidence({
      title: eviTitle,
      file_name: eviFileName,
      file_type: eviFileType,
      uploaded_by: fulfillCompletedBy,
    });
    setEviTitle('');
    setEviFileName('');
  };

  const handleWaiverSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!waiverJustification) return;
    onRequestWaiver({
      request_type: waiverType,
      requested_due_date: waiverDueDate || undefined,
      justification: waiverJustification,
      compensating_controls: compensatingControls || undefined,
      requested_by: fulfillCompletedBy,
    });
    setWaiverJustification('');
    setCompensatingControls('');
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-xl" style={{ height: '92vh' }}>
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', background: '#dbeafe', color: '#1e40af' }}>
                {instance.instance_code}
              </span>
              <span style={{ fontSize: '12px', color: '#64748b' }}>{instance.period_label}</span>
            </div>
            <h3>{instance.obligation_title}</h3>
            <p>Compliance obligation fulfillment tracking and independent verification</p>
          </div>
          <button type="button" onClick={onClose} className="modal-close-btn" title="Kapat" aria-label="Kapat">
            <X size={20} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color, #e2e8f0)', background: 'var(--bg-subtle, #f8fafc)', padding: '0 16px', gap: '8px' }}>
          {(['OVERVIEW', 'FULFILL', 'VERIFY', 'EVIDENCE', 'WAIVERS'] as const).map((tab) => {
            const labels: Record<string, string> = {
              OVERVIEW: 'Overview & Status',
              FULFILL: 'Fulfill Obligation',
              VERIFY: 'Independent Verification',
              EVIDENCE: `Evidence Records (${instance.evidence_records?.length || 0})`,
              WAIVERS: `Waivers & Extensions (${instance.waivers?.length || 0})`,
            };
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: '12px 16px',
                  fontSize: '13px',
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? 'var(--primary-color, #2563eb)' : 'var(--text-muted, #64748b)',
                  borderBottom: isActive ? '2px solid var(--primary-color, #2563eb)' : '2px solid transparent',
                  background: 'transparent',
                  borderTop: 'none',
                  borderLeft: 'none',
                  borderRight: 'none',
                  cursor: 'pointer',
                }}
              >
                {labels[tab]}
              </button>
            );
          })}
        </div>

        {/* Tab Body */}
        <div className="modal-body">
          {activeTab === 'OVERVIEW' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                <div style={{ padding: '12px', border: '1px solid var(--border-color, #e2e8f0)', borderRadius: '8px', background: 'var(--card-bg, #fff)' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Lifecycle Status</span>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary, #0f172a)' }}>{instance.lifecycle_status}</span>
                </div>
                <div style={{ padding: '12px', border: '1px solid var(--border-color, #e2e8f0)', borderRadius: '8px', background: 'var(--card-bg, #fff)' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Verification Status</span>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary, #0f172a)' }}>{instance.verification_status}</span>
                </div>
                <div style={{ padding: '12px', border: '1px solid var(--border-color, #e2e8f0)', borderRadius: '8px', background: 'var(--card-bg, #fff)' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Due Date</span>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary, #0f172a)' }}>{instance.due_date?.slice(0, 10) || '-'}</span>
                </div>
                <div style={{ padding: '12px', border: '1px solid var(--border-color, #e2e8f0)', borderRadius: '8px', background: 'var(--card-bg, #fff)' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Assigned Executor</span>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary, #0f172a)' }}>{instance.assigned_executor_name || '-'}</span>
                </div>
              </div>

              {instance.fulfillment_notes && (
                <div style={{ padding: '16px', background: 'var(--bg-subtle, #f8fafc)', border: '1px solid var(--border-color, #e2e8f0)', borderRadius: '8px' }}>
                  <h4 style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px 0' }}>Fulfillment Execution Notes</h4>
                  <p style={{ fontSize: '13px', color: 'var(--text-primary, #0f172a)', margin: 0, lineHeight: 1.5 }}>{instance.fulfillment_notes}</p>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', marginTop: '8px', display: 'block' }}>
                    Completed Date: {instance.completed_at ? instance.completed_at.slice(0, 10) : '-'}
                  </span>
                </div>
              )}

              {instance.verification_notes && (
                <div style={{ padding: '16px', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '8px' }}>
                  <h4 style={{ fontSize: '12px', fontWeight: 700, color: '#065f46', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px 0' }}>
                    Independent Verification Assessment ({instance.verification_status})
                  </h4>
                  <p style={{ fontSize: '13px', color: '#064e3b', margin: 0, lineHeight: 1.5 }}>{instance.verification_notes}</p>
                  <span style={{ fontSize: '11px', color: '#047857', marginTop: '8px', display: 'block' }}>
                    Verifier: {instance.verifier_name} — {instance.verified_at ? instance.verified_at.slice(0, 10) : '-'}
                  </span>
                </div>
              )}
            </div>
          )}

          {activeTab === 'FULFILL' && (
            <form onSubmit={handleFulfillSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ padding: '12px 16px', background: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                <strong style={{ fontSize: '13px', color: '#1e40af', display: 'block' }}>Submit Fulfillment</strong>
                <span style={{ fontSize: '12px', color: '#3b82f6' }}>Confirm control execution and upload relevant evidence for verification.</span>
              </div>
              <div className="form-field">
                <label>Completed By</label>
                <input
                  type="text"
                  required
                  value={fulfillCompletedBy}
                  onChange={(e) => setFulfillCompletedBy(e.target.value)}
                  placeholder="Full name of person who fulfilled control"
                />
              </div>
              <div className="form-field">
                <label>Fulfillment Execution Notes</label>
                <textarea
                  rows={4}
                  required
                  value={fulfillNotes}
                  onChange={(e) => setFulfillNotes(e.target.value)}
                  placeholder="Detail how the control was performed, observed results, and any relevant metrics..."
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '8px' }}>
                <button type="submit" className="btn-primary">
                  Approve Fulfillment & Submit for Verification
                </button>
              </div>
            </form>
          )}

          {activeTab === 'VERIFY' && (
            <form onSubmit={handleVerifySubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ padding: '12px 16px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                <strong style={{ fontSize: '13px', color: '#166534', display: 'block' }}>Independent Verification</strong>
                <span style={{ fontSize: '12px', color: '#15803d' }}>Review fulfillment records, check evidence adequacy, and assign audit verdict.</span>
              </div>
              <div className="form-grid-2">
                <div className="form-field">
                  <label>Verification Decision</label>
                  <select
                    value={verifStatus}
                    onChange={(e) => setVerifStatus(e.target.value as any)}
                  >
                    <option value="PASSED">PASSED (Verified & Effective)</option>
                    <option value="FAILED">FAILED (Non-compliant / Insufficient Evidence)</option>
                    <option value="PARTIAL">PARTIAL (Partially Effective / Follow-up Needed)</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>Independent Verifier</label>
                  <input
                    type="text"
                    required
                    value={verifierName}
                    onChange={(e) => setVerifierName(e.target.value)}
                  />
                </div>
              </div>
              <div className="form-field">
                <label>Verification Findings & Notes</label>
                <textarea
                  rows={4}
                  required
                  value={verifNotes}
                  onChange={(e) => setVerifNotes(e.target.value)}
                  placeholder="Assessment methodology, documents examined, and rationale for verdict..."
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '8px' }}>
                <button type="submit" className="btn-primary">
                  Record Verification Decision
                </button>
              </div>
            </form>
          )}

          {activeTab === 'EVIDENCE' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <form onSubmit={handleEvidenceSubmit} style={{ padding: '16px', background: 'var(--bg-subtle, #f8fafc)', border: '1px solid var(--border-color, #e2e8f0)', borderRadius: '8px' }}>
                <h4 style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-primary, #0f172a)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 12px 0' }}>Add Evidence Record</h4>
                <div className="form-grid-2">
                  <div className="form-field">
                    <label>Evidence Title</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Q1 Fuel Monitoring Compliance Sheet"
                      value={eviTitle}
                      onChange={(e) => setEviTitle(e.target.value)}
                    />
                  </div>
                  <div className="form-field">
                    <label>File Name</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. fuel_monitoring_audit.pdf"
                      value={eviFileName}
                      onChange={(e) => setEviFileName(e.target.value)}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
                  <button type="submit" className="btn-primary">
                    + Attach Evidence
                  </button>
                </div>
              </form>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {(!instance.evidence_records || instance.evidence_records.length === 0) ? (
                  <p style={{ fontSize: '13px', color: 'var(--text-muted, #64748b)', textAlign: 'center', padding: '16px' }}>No evidence records uploaded yet.</p>
                ) : (
                  instance.evidence_records.map((evi) => (
                    <div key={evi.id} style={{ border: '1px solid var(--border-color, #e2e8f0)', padding: '12px 16px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--card-bg, #fff)' }}>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary, #0f172a)' }}>{evi.title}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', fontFamily: 'monospace', marginTop: '2px' }}>
                          {evi.file_name} ({evi.file_size_bytes} bytes) — Uploaded by: {evi.uploaded_by}
                        </div>
                      </div>
                      <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: '#ecfdf5', color: '#065f46' }}>
                        Primary Evidence
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'WAIVERS' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <form onSubmit={handleWaiverSubmit} style={{ padding: '16px', background: 'var(--bg-subtle, #f8fafc)', border: '1px solid var(--border-color, #e2e8f0)', borderRadius: '8px' }}>
                <h4 style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-primary, #0f172a)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 12px 0' }}>Request Waiver or Extension</h4>
                <div className="form-grid-2">
                  <div className="form-field">
                    <label>Request Type</label>
                    <select
                      value={waiverType}
                      onChange={(e) => setWaiverType(e.target.value as any)}
                    >
                      <option value="EXTENSION">Due Date Extension</option>
                      <option value="WAIVER">Formal Exception / Waiver</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Requested Target Date</label>
                    <input
                      type="date"
                      value={waiverDueDate}
                      onChange={(e) => setWaiverDueDate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="form-field" style={{ marginTop: '12px' }}>
                  <label>Justification</label>
                  <textarea
                    rows={3}
                    required
                    value={waiverJustification}
                    onChange={(e) => setWaiverJustification(e.target.value)}
                    placeholder="Operational or technical rationale for requesting waiver or extension..."
                  />
                </div>
                <div className="form-field" style={{ marginTop: '12px' }}>
                  <label>Compensating Controls</label>
                  <textarea
                    rows={2}
                    value={compensatingControls}
                    onChange={(e) => setCompensatingControls(e.target.value)}
                    placeholder="Interim mitigating controls in place during the extension period..."
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
                  <button type="submit" className="btn-primary">
                    Submit Request
                  </button>
                </div>
              </form>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {(!instance.waivers || instance.waivers.length === 0) ? (
                  <p style={{ fontSize: '13px', color: 'var(--text-muted, #64748b)', textAlign: 'center', padding: '16px' }}>No waiver requests recorded.</p>
                ) : (
                  instance.waivers.map((w) => (
                    <div key={w.id} style={{ border: '1px solid var(--border-color, #e2e8f0)', padding: '14px', borderRadius: '8px', background: 'var(--card-bg, #fff)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>{w.request_type}</span>
                        <span
                          style={{
                            fontSize: '11px',
                            fontWeight: 600,
                            padding: '2px 8px',
                            borderRadius: '4px',
                            background: w.status === 'APPROVED' ? '#ecfdf5' : '#fef3c7',
                            color: w.status === 'APPROVED' ? '#065f46' : '#92400e',
                          }}
                        >
                          {w.status}
                        </span>
                      </div>
                      <p style={{ fontSize: '12px', color: 'var(--text-secondary, #334155)', margin: 0 }}><strong>Justification:</strong> {w.justification}</p>
                      {w.compensating_controls && (
                        <p style={{ fontSize: '12px', color: '#1e40af', margin: 0 }}><strong>Compensating Controls:</strong> {w.compensating_controls}</p>
                      )}
                      {w.status === 'REQUESTED' && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '8px' }}>
                          <button
                            type="button"
                            onClick={() => onApproveWaiver(w.id, 'Accountable Manager', 'Extension formally approved.')}
                            className="btn-primary"
                          >
                            Approve Waiver Request
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button type="button" onClick={onClose} className="btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
